import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import DoorBackOutlinedIcon from '@mui/icons-material/DoorBackOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from 'react-i18next';

import { formatSpotRegion } from '@shared/utils/assistantScript';
import AssistantScriptsService from '@shared/services/AssistantScriptsService';
import AssistantRagStorageService from '@shared/services/AssistantRagStorageService';

import { useMytaverseEvent } from '@core/dashboard/providers';
import DynamicMapTriggersService from '@services/DynamicMapTriggersService';

import AiInspectorHeader from '../AiScripts/AiInspectorHeader';
import AssistantTester from '../AiScripts/AssistantTester';
import KnowledgeTab from '../AiScripts/KnowledgeTab';
import LiveBrandingStrip from '../AiScripts/LiveBrandingStrip';
import ScriptTab from '../AiScripts/ScriptTab';
import type { AiEntity } from '../AiScripts/types';
import { useAiInspectorEntity } from '../AiScripts/useAiInspectorEntity';
import { useAiInspectorStyles } from '../AiScripts/inspectorStyles';
import { useEditorMode } from '../EditorModeContext';
import { useDrift } from '../DriftContext';
import { useEditorAiData } from '../AiScripts/EditorAiDataContext';

interface IAIScriptInspectorProps {
  trigger: string;
  scriptId: string | null;
}

type Tab = 'script' | 'knowledge' | 'test';

const TABS: { id: Tab; labelKey: string; labelFallback: string }[] = [
  { id: 'script', labelKey: 'editorMode.aiTabScript', labelFallback: 'Script' },
  {
    id: 'knowledge',
    labelKey: 'editorMode.aiTabKnowledge',
    labelFallback: 'Knowledge',
  },
  { id: 'test', labelKey: 'editorMode.aiTabTest', labelFallback: 'Test' },
];

const scriptDriftStyles = {
  amber: {
    display: 'flex',
    gap: '9px',
    padding: '10px 12px',
    borderRadius: '11px',
    border: '1px solid rgba(245, 166, 35, 0.35)',
    backgroundColor: 'rgba(245, 166, 35, 0.14)',
    color: '#f6d49a',
    marginBottom: '12px',
  },
  // Shares the banner's left column with the info icon — the refresh icon
  // sits at the top, the delete button at the bottom, matching the height of
  // the text next to them (justifyContent: 'space-between' spreads the two
  // apart within the column's stretched height). Deliberately icon-only
  // rather than a wide labeled button — deleting a dynamic trigger is a rare
  // cleanup action, not something that should compete visually with the
  // script/knowledge editing controls above it.
  iconColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  cornerDeleteButton: {
    width: 22,
    height: 22,
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#f6d49a',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.12s ease',
    '&:hover': {
      color: '#f06d6d',
      backgroundColor: 'rgba(240, 109, 109, 0.13)',
    },
  },
};

const AIScriptInspector = ({ trigger, scriptId }: IAIScriptInspectorProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();
  const { clearSelection, aiTreeSection } = useEditorMode();
  const { triggerDrift } = useDrift();
  const { refetchAll } = useEditorAiData();
  const { setDynamicAiTriggers } = useMytaverseEvent();

  const {
    entity,
    ragRefetch,
    mapName,
    mapId,
    brandingId,
    isLiveBranding,
    isDynamicTrigger,
    createCategory,
    createPrimary,
    createTriggers,
  } = useAiInspectorEntity({ trigger, scriptId });

  const [tab, setTab] = useState<Tab>('script');
  const [dynamicDeleteOpen, setDynamicDeleteOpen] = useState(false);
  const [dynamicDeleteBusy, setDynamicDeleteBusy] = useState(false);

  const handleClose = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // When no script + no RAG items exist yet for this trigger (or both exist
  // but in a different branding/map scope), we still want the inspector to
  // surface Create script / Add knowledge — never "Entity unavailable", which
  // is a dead end the user can't recover from. The synthetic entity inherits
  // the active mode's category so the header chip reads correctly (TRIGGER for
  // an AI trigger being created, SPOT for a spot).
  const effectiveEntity = useMemo<AiEntity>(() => {
    const kindFor = createCategory === 'spot' ? 'ui' : 'area';
    if (entity) {
      // A trigger/spot can have knowledge but no script yet — aggregateEntities
      // surfaces that as a knowledge-only (ctx) entity. When the user opened it
      // from the Scripts section (not browsing Knowledge), present it as a
      // trigger/spot so the Script / Knowledge / Test tabs render — with
      // "Create script" and the attached knowledge — instead of the
      // knowledge-only layout. The category keeps the header chip + create
      // trigger type correct (region → area_entry, spot → ui_click).
      if (entity.kind === 'ctx' && aiTreeSection !== 'knowledge') {
        return {
          ...entity,
          kind: kindFor,
          category: createCategory,
          primaryTrigger: entity.primaryTrigger || createPrimary,
        };
      }
      return entity;
    }
    return {
      id: `synthetic:${trigger}`,
      kind: kindFor,
      name: formatSpotRegion(createPrimary) || createPrimary || trigger,
      primaryTrigger: createPrimary || trigger,
      aliases: [],
      script: null,
      contextItems: [],
      scriptState: 'none',
      contextState: 'none',
      overallState: 'empty',
      category: createCategory,
    };
  }, [entity, trigger, aiTreeSection, createCategory, createPrimary]);

  // The Test tab probes how Noa answers against the configured knowledge — it
  // is meaningless without any, so gate it on the entity having context items.
  const hasKnowledge = effectiveEntity.contextItems.length > 0;
  // Defensive: if the user is on the Test tab and then removes the last
  // knowledge item, fall back to the Script tab instead of showing an
  // orphaned tester.
  const activeTab: Tab = tab === 'test' && !hasKnowledge ? 'script' : tab;

  const driftScriptId = effectiveEntity.script?.scriptId ?? null;
  const isNewTrigger = useMemo(
    () =>
      !effectiveEntity.script &&
      triggerDrift.rows.some(
        (row) =>
          row.status === 'new' &&
          row.trigger.toLowerCase() ===
            (effectiveEntity.primaryTrigger || trigger).toLowerCase(),
      ),
    [effectiveEntity, trigger, triggerDrift],
  );

  // Cascades: deletes the script and every knowledge item bound to this
  // trigger (already correctly resolved by the aggregator into
  // effectiveEntity), then the dynamic-trigger registry row itself.
  const handleDeleteDynamicTrigger = useCallback(async () => {
    if (!mapId) {
      return;
    }
    setDynamicDeleteBusy(true);
    try {
      await Promise.all([
        driftScriptId
          ? AssistantScriptsService.deleteAssistantScript(driftScriptId)
          : Promise.resolve(),
        ...effectiveEntity.contextItems.map((item) =>
          AssistantRagStorageService.deleteAssistantRagStorageItem(
            item.ragVectorLinkId,
          ),
        ),
      ]);
      await DynamicMapTriggersService.deleteDynamicMapTrigger(
        mapId,
        effectiveEntity.primaryTrigger || trigger,
      );
      setDynamicAiTriggers((prev) =>
        prev.filter(
          (t) =>
            t.triggerName.toLowerCase() !==
            (effectiveEntity.primaryTrigger || trigger).toLowerCase(),
        ),
      );
      await refetchAll();
      setDynamicDeleteOpen(false);
      clearSelection();
    } catch (deleteError) {
      console.error(
        `Failed to delete dynamic map trigger: ${
          (deleteError as Error).message
        }`,
        { mapId, triggerName: effectiveEntity.primaryTrigger || trigger },
      );
    } finally {
      setDynamicDeleteBusy(false);
    }
  }, [
    mapId,
    driftScriptId,
    effectiveEntity,
    trigger,
    setDynamicAiTriggers,
    refetchAll,
    clearSelection,
  ]);

  // Dynamic and new-in-build triggers only. A script whose region the build dropped used to get a red
  // banner and a "remove script" button here — removed with the rest of the orphan warnings, since it
  // reported a build change to someone who only authors scripts.
  const renderDriftBanner = () => {
    if (isDynamicTrigger) {
      return (
        <Box sx={scriptDriftStyles.amber}>
          <Box sx={scriptDriftStyles.iconColumn}>
            <AutorenewOutlinedIcon sx={{ fontSize: 16 }} />
            <Tooltip title={translate('editorMode.deleteDynamicTrigger')}>
              <Box
                component="button"
                type="button"
                sx={scriptDriftStyles.cornerDeleteButton}
                onClick={() => setDynamicDeleteOpen(true)}
                aria-label={translate('editorMode.deleteDynamicTrigger')}
                data-testid="ai-inspector-delete-dynamic-trigger-btn"
              >
                <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
          </Box>
          <Typography sx={{ fontSize: '12px', lineHeight: 1.45 }}>
            {translate('editorMode.dynamicTriggerBanner')}
          </Typography>
        </Box>
      );
    }
    if (isNewTrigger) {
      return (
        <Box sx={scriptDriftStyles.amber}>
          <AutorenewOutlinedIcon
            sx={{ fontSize: 16, flexShrink: 0, mt: '1px' }}
          />
          <Typography sx={{ fontSize: '12px', lineHeight: 1.45 }}>
            {translate('editorMode.driftNewTriggerBanner')}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderDynamicDeleteDialog = () => (
    <Dialog
      open={dynamicDeleteOpen}
      onClose={() => setDynamicDeleteOpen(false)}
      data-testid="dynamic-trigger-delete-dialog"
      PaperProps={{
        sx: {
          background:
            'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
          borderRadius: '16px',
          color: '#e2e4ed',
          '& .MuiDialogTitle-root': { color: '#e2e4ed' },
          '& .MuiDialogContentText-root': { color: '#e2e4ed' },
          '& .MuiButton-text': { color: '#e2e4ed' },
          '& .MuiButton-textError': { color: '#f06d6d' },
        },
      }}
    >
      <DialogTitle>{translate('editorMode.deleteDynamicTrigger')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {translate('editorMode.deleteDynamicTriggerConfirm')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setDynamicDeleteOpen(false)}
          disabled={dynamicDeleteBusy}
          data-testid="dynamic-trigger-delete-dialog-cancel-btn"
        >
          {translate('common.cancel', 'Cancel')}
        </Button>
        <Button
          color="error"
          onClick={handleDeleteDynamicTrigger}
          disabled={dynamicDeleteBusy}
          data-testid="dynamic-trigger-delete-dialog-confirm-btn"
        >
          {translate('common.delete', 'Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Knowledge-only entities skip the tab strip; the spec stacks regions card +
  // knowledge card + tester directly because there is nothing to put under the
  // Script tab.
  if (effectiveEntity.kind === 'ctx') {
    return (
      <Box sx={classes.panel}>
        <AiInspectorHeader entity={effectiveEntity} onClose={handleClose} />
        <Box sx={classes.body}>
          {isLiveBranding ? <LiveBrandingStrip /> : null}
          {renderDriftBanner()}
          <Box sx={classes.card}>
            <Box sx={classes.cardHeader}>
              <Typography component="span" sx={classes.cardHeaderTitle}>
                <DoorBackOutlinedIcon sx={{ fontSize: 13 }} />
                {translate('editorMode.aiAvailableInRegions')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {effectiveEntity.contextItems
                .flatMap((item) => item.tags)
                .filter((tag, index, arr) => arr.indexOf(tag) === index)
                .map((tag) => (
                  <Box key={tag} component="span" sx={classes.regionChip}>
                    <DoorBackOutlinedIcon />
                    {tag}
                  </Box>
                ))}
            </Box>
          </Box>
          <KnowledgeTab
            entity={effectiveEntity}
            brandingId={brandingId}
            mapName={mapName}
            onChanged={ragRefetch}
          />
          <Box sx={classes.card}>
            <Box sx={classes.cardHeader}>
              <Typography component="span" sx={classes.cardHeaderTitle}>
                {translate('editorMode.aiTabTest', 'Test')}
              </Typography>
            </Box>
            <AssistantTester
              entity={effectiveEntity}
              brandingId={brandingId}
              mapName={mapName}
            />
          </Box>
        </Box>
        {renderDynamicDeleteDialog()}
      </Box>
    );
  }

  return (
    <Box sx={classes.panel}>
      <AiInspectorHeader entity={effectiveEntity} onClose={handleClose} />
      <Box sx={classes.body}>
        {isLiveBranding ? <LiveBrandingStrip /> : null}
        {renderDriftBanner()}
        <Box sx={classes.tabs} role="tablist">
          {TABS.map((definition) => {
            const disabled = definition.id === 'test' && !hasKnowledge;
            const tabButton = (
              <Box
                component="button"
                type="button"
                role="tab"
                aria-selected={activeTab === definition.id}
                aria-disabled={disabled}
                disabled={disabled}
                sx={[
                  classes.tab,
                  activeTab === definition.id && classes.tabActive,
                  disabled && classes.tabDisabled,
                ]}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  setTab(definition.id);
                }}
                data-testid={`ai-inspector-tab-${definition.id}`}
              >
                {translate(definition.labelKey, definition.labelFallback)}
              </Box>
            );
            if (disabled) {
              return (
                <Tooltip
                  key={definition.id}
                  title={translate('editorMode.aiTestNoKnowledge')}
                >
                  <Box component="span" sx={{ flex: 1, display: 'flex' }}>
                    {tabButton}
                  </Box>
                </Tooltip>
              );
            }
            return (
              <React.Fragment key={definition.id}>{tabButton}</React.Fragment>
            );
          })}
        </Box>

        {activeTab === 'script' ? (
          <ScriptTab
            entity={effectiveEntity}
            trigger={effectiveEntity.primaryTrigger || trigger}
            createTriggers={createTriggers}
            mapName={mapName}
            brandingId={brandingId}
            onScriptChanged={ragRefetch}
          />
        ) : null}
        {activeTab === 'knowledge' ? (
          <KnowledgeTab
            entity={effectiveEntity}
            brandingId={brandingId}
            mapName={mapName}
            onChanged={ragRefetch}
          />
        ) : null}
        {activeTab === 'test' ? (
          <Box sx={classes.card}>
            <AssistantTester
              entity={effectiveEntity}
              brandingId={brandingId}
              mapName={mapName}
            />
          </Box>
        ) : null}
      </Box>
      {renderDynamicDeleteDialog()}
    </Box>
  );
};

export default AIScriptInspector;
